import {
  useOrganisationList,
  useSite,
} from "@inrange/building-manager-api-client";
import { formatPercentage } from "@inrange/theme-components/formatting";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import {
  Site,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  buildSiteCalculationPayload,
  useContextTS,
} from "@inrange/shared-components";
import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useReactQueryError } from "../../../utils/react-query-error";
import CollapsibleCard from "../CollapsibleCard";
import { currencyValue, formatNumber2dp, parseAsNumber } from "./utils";

const FinancialPerformanceCustomer = ({
  site,
  isOwnerOccupied,
}: {
  site: SiteAllowStringValues;
  isOwnerOccupied: boolean;
}) => {
  const [isReportDebug, setIsReportDebug] = useState(false);
  const [customerReportPeriod, setCustomerReportPeriod] = useState("annual");
  const { siteCalculations, setClientsideValidations } = useContextTS(
    SiteCalculationsContext
  );
  const { fetchSiteReportPreview } = useSite({
    app: "admin",
  });
  useReactQueryError(fetchSiteReportPreview, "Error downloading report");
  const { fetchOrganisations } = useOrganisationList();
  const orgsById = (fetchOrganisations.data?.organisations || []).reduce(
    (
      acc: { [key: string]: { minIRR?: number } },
      org: { id: string; minIRR?: number }
    ) => {
      acc[org.id] = org;
      return acc;
    },
    {}
  );
  const minIRR = (site.siteOwnerships || [])
    .filter(
      (so) =>
        (so.ownership === "landlord" || so.ownership === "ownerOccupier") &&
        orgsById[so.orgID] !== undefined &&
        orgsById[so.orgID].minIRR !== undefined
    )
    .map((so) => orgsById[so.orgID].minIRR)
    .reduce((acc, minIRR) => {
      if (acc === undefined) {
        return minIRR;
      }
      // If there are multiple minIRR values, return the highest one
      return minIRR !== undefined && minIRR > acc ? minIRR : acc;
    }, undefined);
  const ownershipKey = isOwnerOccupied ? "ownerOccupier" : "landlord";
  const isIRROutOfBounds =
    parseAsNumber(site.totalInstalledCapacity) > 0 &&
    minIRR &&
    (!siteCalculations.financialModels?.[ownershipKey]?.license?.irr ||
      siteCalculations.financialModels?.[ownershipKey]?.license?.irr < minIRR);
  useEffect(() => {
    setClientsideValidations((prev) => ({
      ...prev,
      isIRROutOfBounds: isIRROutOfBounds ? "true" : undefined,
    }));
  }, [isIRROutOfBounds, setClientsideValidations]);
  const showLeaseFields =
    site.investmentModel === "lease" || site.investmentModel === "none";
  const showLicenseFields =
    site.investmentModel === "license" || site.investmentModel === "none";

  return (
    <CollapsibleCard
      id="financial-performance-customer"
      title={"Customer financial performance"}
      className="mt-2"
      menu={
        <Dropdown>
          <Dropdown.Toggle
            variant="secondary"
            id="dropdown-basic"
            style={{ paddingTop: "2px", paddingBottom: "2px" }}
          >
            Downloads
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {!isOwnerOccupied && (
              <>
                {showLicenseFields && (
                  <Dropdown.Item
                    onClick={() =>
                      fetchSiteReportPreview.mutate({
                        reportName: "customer_cashflow",
                        fileName: "cashflow",
                        siteName: site.name,
                        options: {
                          cashflowType: "landlord-license",
                          periodType: customerReportPeriod,
                          ...(isReportDebug && { debug: true }),
                        },
                        payload: {
                          site: buildSiteCalculationPayload({
                            site: site as unknown as Partial<Site>,
                            clearNonCalcFields: false,
                          }),
                        },
                      })
                    }
                  >
                    Customer cashflow (Landlord, License)
                  </Dropdown.Item>
                )}
                {showLeaseFields && (
                  <Dropdown.Item
                    onClick={() =>
                      fetchSiteReportPreview.mutate({
                        reportName: "customer_cashflow",
                        fileName: "cashflow",
                        siteName: site.name,
                        options: {
                          cashflowType: "landlord-lease",
                          periodType: customerReportPeriod,
                          ...(isReportDebug && { debug: true }),
                        },
                        payload: {
                          site: buildSiteCalculationPayload({
                            site: site as unknown as Partial<Site>,
                            clearNonCalcFields: false,
                          }),
                        },
                      })
                    }
                  >
                    Customer cashflow (Landlord, Lease)
                  </Dropdown.Item>
                )}
              </>
            )}
            {isOwnerOccupied && (
              <>
                {showLicenseFields && (
                  <Dropdown.Item
                    onClick={() =>
                      fetchSiteReportPreview.mutate({
                        reportName: "customer_cashflow",
                        fileName: "cashflow",
                        siteName: site.name,
                        options: {
                          cashflowType: "ownerOccupier-license",
                          periodType: customerReportPeriod,
                          ...(isReportDebug && { debug: true }),
                        },
                        payload: {
                          site: buildSiteCalculationPayload({
                            site: site as unknown as Partial<Site>,
                            clearNonCalcFields: false,
                          }),
                        },
                      })
                    }
                  >
                    Customer cashflow (Owner-Occupier, License)
                  </Dropdown.Item>
                )}
                {showLeaseFields && (
                  <Dropdown.Item
                    onClick={() =>
                      fetchSiteReportPreview.mutate({
                        reportName: "customer_cashflow",
                        fileName: "cashflow",
                        siteName: site.name,
                        options: {
                          cashflowType: "ownerOccupier-lease",
                          periodType: customerReportPeriod,
                          ...(isReportDebug && { debug: true }),
                        },
                        payload: {
                          site: buildSiteCalculationPayload({
                            site: site as unknown as Partial<Site>,
                            clearNonCalcFields: false,
                          }),
                        },
                      })
                    }
                  >
                    Customer cashflow (Owner-Occupier, Lease)
                  </Dropdown.Item>
                )}
              </>
            )}
            {showLicenseFields && (
              <>
                <Dropdown.Item
                  onClick={() =>
                    fetchSiteReportPreview.mutate({
                      reportName: "prologis_cashflow",
                      fileName: "prologis-cashflow",
                      siteName: site.name,
                      options: {
                        version: "v2",
                        ...(isReportDebug && { debug: true }),
                      },
                      payload: {
                        site: buildSiteCalculationPayload({
                          site: site as unknown as Partial<Site>,
                          clearNonCalcFields: false,
                        }),
                      },
                    })
                  }
                >
                  Prologis cashflow (P90)
                  {site.pvForecastProbability !== "P90" && (
                    <>
                      <br />
                      ℹ️ Warning: Numbers in report will differ from <br />
                      on-screen as download always uses P90 generation.
                    </>
                  )}
                </Dropdown.Item>
              </>
            )}
            <Dropdown.Item
              onClick={() =>
                fetchSiteReportPreview.mutate({
                  reportName: "amazon_cashflow",
                  fileName: "amazon-cashflow",
                  siteName: site.name,
                  payload: {
                    site: buildSiteCalculationPayload({
                      site: site as unknown as Partial<Site>,
                      clearNonCalcFields: false,
                    }),
                  },
                })
              }
            >
              Amazon cashflow
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() =>
                fetchSiteReportPreview.mutate({
                  reportName: "amazon_ppa_annex_a",
                  fileName: "amazon-ppa-annex-a",
                  siteName: site.name,
                  payload: {
                    site: buildSiteCalculationPayload({
                      site: site as unknown as Partial<Site>,
                      clearNonCalcFields: false,
                    }),
                  },
                })
              }
            >
              Download Amazon PPA Annex A report
            </Dropdown.Item>
            <Dropdown.Item
              as="div"
              onClick={(e) => {
                e.stopPropagation();
                setIsReportDebug(!isReportDebug);
              }}
            >
              <Form.Check
                type="switch"
                label="Enable debug data in reports"
                checked={isReportDebug}
                readOnly
              />
            </Dropdown.Item>
            <Dropdown.Item
              as="div"
              onClick={(e) => {
                e.stopPropagation();
                setCustomerReportPeriod(
                  customerReportPeriod === "annual" ? "monthly" : "annual"
                );
              }}
            >
              <Form.Check
                type="switch"
                label="Monthly customer cashflow"
                checked={customerReportPeriod === "monthly"}
                readOnly
              />
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      }
    >
      <Row className="d-flex flex-wrap align-items-end">
        {showLicenseFields && (
          <>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="financialModelIrr">
                <Form.Label>IRR (%)</Form.Label>
                <Form.Control
                  disabled
                  value={
                    siteCalculations.financialModels?.[ownershipKey]?.license
                      ?.irr !== undefined
                      ? formatNumber2dp(
                          siteCalculations.financialModels?.[ownershipKey]
                            ?.license?.irr * 100
                        )
                      : "N/A"
                  }
                  isInvalid={!!isIRROutOfBounds}
                  data-testid="financial-model-irr"
                />
                <Form.Control.Feedback type="invalid">
                  {isIRROutOfBounds
                    ? `IRR must be at least ${formatPercentage(minIRR, 2)}`
                    : undefined}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="paybackMonths">
                <Form.Label>Payback (months)</Form.Label>
                <Form.Control
                  disabled
                  value={
                    siteCalculations.financialModels?.[ownershipKey]?.license
                      ?.paybackMonths || "N/A"
                  }
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="licenseRevenueY1">
                <Form.Label>License rev (Y1)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    siteCalculations.financialModels?.[ownershipKey]?.license
                      ?.revenue || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="licenseRevenueLT">
                <Form.Label>License rev (LT)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    siteCalculations.financialModels?.[ownershipKey]?.license
                      ?.lifetimeRevenue || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
          </>
        )}
        {showLeaseFields && (
          <>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="leaseReveueY1">
                <Form.Label>Lease revenue (Y1)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    siteCalculations.financialModels?.[ownershipKey]?.lease
                      ?.revenue || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="leaseRevenueLT">
                <Form.Label>Lease revenue (LT)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    siteCalculations.financialModels?.[ownershipKey]?.lease
                      ?.lifetimeRevenue || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
          </>
        )}
        {ownershipKey === "landlord" && (
          <>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="tenantCostY1">
                <Form.Label>Tenant cost (Y1)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    siteCalculations.financialModels?.procurement?.costTotal ||
                      0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="tenantSavingsY1">
                <Form.Label>Savings vs market (Y1)</Form.Label>
                <Form.Control
                  disabled
                  value={currencyValue(
                    siteCalculations.financialModels?.procurement?.savings || 0,
                    siteCalculations.currencyCode
                  )}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId="tenantSavingsVsMarketY1">
                <Form.Label>Savings vs market (Y1, %)</Form.Label>
                <Form.Control
                  disabled
                  value={
                    siteCalculations.financialModels?.marketPrice || 0
                      ? formatNumber2dp(
                          ((siteCalculations.financialModels?.procurement
                            ?.savings || 0) /
                            (siteCalculations.financialModels?.marketPrice ||
                              0)) *
                            100
                        )
                      : "0.00"
                  }
                />
              </Form.Group>
            </Col>
            {(
              siteCalculations.financialModels?.procurement?.savingsPerPpa || []
            ).map((savingsForPpa, index) => (
              <React.Fragment key={index}>
                <Col sm={4}>
                  <Form.Group
                    className="mb-3"
                    controlId={`tenantSavingsPPA${index + 1}`}
                  >
                    <Form.Label>Savings vs market (PPA {index + 1})</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        savingsForPpa ?? 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group
                    className="mb-3"
                    controlId={`tenantSavingsVsMarketPPA${index + 1}`}
                  >
                    <Form.Label>
                      Savings vs market (PPA {index + 1}, %)
                    </Form.Label>
                    <Form.Control
                      disabled
                      value={formatNumber2dp(
                        ((savingsForPpa ?? 0) /
                          (siteCalculations.financialModels
                            ?.marketPricePerPpa?.[index] || 0)) *
                          100
                      )}
                    />
                  </Form.Group>
                </Col>
              </React.Fragment>
            ))}
          </>
        )}
        {ownershipKey === "ownerOccupier" && (
          <>
            {showLicenseFields && (
              <>
                <Col sm={4}>
                  <Form.Group className="mb-3" controlId="licenseOOCostY1">
                    <Form.Label>License OO cost (Y1)</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        siteCalculations.financialModels?.ooLicenseProcurement
                          ?.costTotal || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group className="mb-3" controlId="licenseOOSavingsY1">
                    <Form.Label>License OO savings vs market (Y1)</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        siteCalculations.financialModels?.ooLicenseProcurement
                          ?.savings || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group
                    className="mb-3"
                    controlId="licenseOOSavingsVsMarketY1"
                  >
                    <Form.Label>
                      License OO savings vs market (Y1, %)
                    </Form.Label>
                    <Form.Control
                      disabled
                      value={
                        (
                          ((siteCalculations.financialModels
                            ?.ooLicenseProcurement.savings || 0) /
                            (siteCalculations.financialModels?.marketPrice ||
                              0)) *
                          100
                        ).toFixed(2) + "%"
                      }
                    />
                  </Form.Group>
                </Col>
                {(
                  siteCalculations.financialModels?.ooLicenseProcurement
                    ?.savingsPerPpa || []
                ).map((savingsForPpa, index) => (
                  <React.Fragment key={index}>
                    <Col sm={4}>
                      <Form.Group
                        className="mb-3"
                        controlId={`licenseOOSavingsPPA${index + 1}`}
                      >
                        <Form.Label>
                          License OO sav. vs market (PPA {index + 1})
                        </Form.Label>
                        <Form.Control
                          disabled
                          value={currencyValue(
                            savingsForPpa,
                            siteCalculations.currencyCode
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group
                        className="mb-3"
                        controlId={`licenseOOSavingsVsMarketPPA${index + 1}`}
                      >
                        <Form.Label>
                          License OO sav. vs market (PPA {index + 1}, %)
                        </Form.Label>
                        <Form.Control
                          disabled
                          value={
                            (
                              (savingsForPpa /
                                (siteCalculations.financialModels
                                  ?.marketPricePerPpa?.[index] || 0)) *
                              100
                            ).toFixed(2) + "%"
                          }
                        />
                      </Form.Group>
                    </Col>
                  </React.Fragment>
                ))}
              </>
            )}
            {showLeaseFields && (
              <>
                <Col sm={4}>
                  <Form.Group className="mb-3" controlId="leaseOOCostY1">
                    <Form.Label>Lease OO cost (Y1)</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        siteCalculations.financialModelsInrange?.defaultRevenue
                          ?.grossEnergyRevenue || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group className="mb-3" controlId="leaseOOSavingsY1">
                    <Form.Label>Lease OO savings vs market (Y1)</Form.Label>
                    <Form.Control
                      disabled
                      value={currencyValue(
                        siteCalculations.financialModels?.procurement
                          ?.savings || 0,
                        siteCalculations.currencyCode
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group
                    className="mb-3"
                    controlId="leaseOOSavingsVsMarketY1"
                  >
                    <Form.Label>Lease OO savings vs market (Y1, %)</Form.Label>
                    <Form.Control
                      disabled
                      value={
                        (
                          ((siteCalculations.financialModels?.procurement
                            ?.savings || 0) /
                            (siteCalculations.financialModels?.marketPrice ||
                              0)) *
                          100
                        ).toFixed(2) + "%"
                      }
                    />
                  </Form.Group>
                </Col>
                {(
                  siteCalculations.financialModels?.procurement
                    ?.savingsPerPpa || []
                ).map((savingsForPpa, index) => (
                  <React.Fragment key={index}>
                    <Col sm={4}>
                      <Form.Group
                        className="mb-3"
                        controlId={`leaseOOSavingsPPA${index + 1}`}
                      >
                        <Form.Label>
                          Lease OO sav. vs market (PPA {index + 1})
                        </Form.Label>
                        <Form.Control
                          disabled
                          value={currencyValue(
                            savingsForPpa,
                            siteCalculations.currencyCode
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group
                        className="mb-3"
                        controlId={`leaseOOSavingsVsMarketPPA${index + 1}`}
                      >
                        <Form.Label>
                          Lease OO sav. vs market (PPA {index + 1}, %)
                        </Form.Label>
                        <Form.Control
                          disabled
                          value={
                            (
                              (savingsForPpa /
                                (siteCalculations.financialModels
                                  ?.marketPricePerPpa?.[index] || 0)) *
                              100
                            ).toFixed(2) + "%"
                          }
                        />
                      </Form.Group>
                    </Col>
                  </React.Fragment>
                ))}
              </>
            )}
          </>
        )}
      </Row>
    </CollapsibleCard>
  );
};

export default FinancialPerformanceCustomer;
